import { h, clear, toast } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Eye, EyeOff, LogIn, ArrowLeft, Send } from 'lucide';
import { AuthAPI, BranchAPI } from '../services/endpoints.js';
import { Token, CurrentUser } from '../services/api.js';
import { navigate } from '../utils/router.js';
import { validateForm, showFieldErrors, required, email, minLen } from '../utils/validators.js';

const loginRules = {
  email:    [(v) => required(v, 'El correo electrónico'), email],
  password: [(v) => required(v, 'La contraseña')],
};

export const LoginPage = async () => {
  const view = document.getElementById('view');
  clear(view);

  const card = h('div', { class: 'auth-card card' },
    h('h1', { style: { margin: '0 0 4px 0', color: 'var(--c-accent)' } }, 'TIQUETERA'),
    h('p', { class: 'muted', style: { marginTop: 0 } }, 'Inicia sesión para administrar tu plataforma de ticketing.'),
    h('form', { onsubmit: handleLogin },
      h('label', {}, 'Correo electrónico'),
      h('input', { name: 'email', type: 'email', placeholder: 'tu@ejemplo.com', autocomplete: 'username', required: true }), // [PLACEHOLDER] Replace example email placeholder with client-appropriate text
      h('label', {}, 'Contraseña'),
      h('div', { class: 'password-wrap' },
        h('input', { name: 'password', type: 'password', placeholder: '••••••••', autocomplete: 'current-password', required: true }),
        h('button', {
          type: 'button',
          class: 'password-toggle',
          'aria-label': 'Mostrar contraseña',
          onclick: function() {
            const input = this.parentElement.querySelector('input');
            const isPassword = input.type === 'password';
            input.type = isPassword ? 'text' : 'password';
            const icon = this.querySelector('.eye-icon');
            const iconOff = this.querySelector('.eye-off-icon');
            if (icon && iconOff) {
              icon.style.display = isPassword ? '' : 'none';
              iconOff.style.display = isPassword ? 'none' : '';
            }
            this.setAttribute('aria-label', isPassword ? 'Ocultar contraseña' : 'Mostrar contraseña');
          }
        },
          h('span', { class: 'eye-icon', html: iconSvg(Eye, 18) }),
          h('span', { class: 'eye-off-icon', style: 'display:none', html: iconSvg(EyeOff, 18) }),
        ),
      ),
      h('button', { type: 'submit', class: 'btn btn-block mt-16' }, h('span', { html: iconSvg(LogIn, 16) }), 'Iniciar sesión'),
    ),
    h('div', { style: { marginTop: '20px', textAlign: 'center' } },
      h('a', { href: 'javascript:void(0)', onclick: (e) => { e.preventDefault(); navigate('/register'); } }, 'Registrarse como beneficiario →'),
    )
  );

  view.appendChild(h('div', { class: 'auth-wrap' }, card));

  async function handleLogin(e) {
    e.preventDefault();
    const values = {
      email: card.querySelector('[name="email"]').value.trim(),
      password: card.querySelector('[name="password"]').value,
    };
    const errors = validateForm(values, loginRules);
    if (Object.keys(errors).length) { showFieldErrors(card, errors); return; }
    try {
      const res = await AuthAPI.login(values.email, values.password);
      Token.set(res.data.token);
      CurrentUser.set(res.data.user);
      toast('¡Bienvenido de nuevo!', 'success');
      navigate('/dashboard');
    } catch (err) {
      toast(err.message, 'error');
    }
  }
};

const registerRules = {
  full_name: [(v) => required(v, 'El nombre completo'), minLen(2, 'El nombre completo')],
  email:     [(v) => required(v, 'El correo electrónico'), email],
  branch_id: [(v) => required(String(v), 'La sucursal')],
  document_number: [(v) => required(v, 'El número de documento')],
  phone:     [(v) => required(v, 'El teléfono')],
};

export const RegisterPage = async () => {
  const view = document.getElementById('view');
  clear(view);

  let branches = [];
  try {
    const brRes = await BranchAPI.listPublic();
    branches = brRes.data || [];
  } catch (err) {
    console.error('Failed to load register form data', err);
    toast(`Error al cargar sucursales: ${err.message}`, 'error');
    branches = [];
  }

  const card = h('div', { class: 'auth-card card' },
    h('h1', { style: { margin: '0 0 4px 0', color: 'var(--c-accent)' } }, 'Solicitar ingreso'),
    h('p', { class: 'muted', style: { marginTop: 0 } }, 'Completa este formulario para solicitar tu ingreso a una sucursal (escuela). Un administrador revisará y aprobará tu solicitud.'),
    h('div', {},
      h('label', {}, 'Nombre completo'),
      h('input', { name: 'full_name', type: 'text', autocomplete: 'name', required: true }),
      h('label', {}, 'Correo electrónico'),
      h('input', { name: 'email', type: 'email', autocomplete: 'email', required: true }),
      h('label', {}, 'Número de documento'),
      h('input', { name: 'document_number', type: 'text', autocomplete: 'off', required: true }),
      h('label', {}, 'NIT (opcional)'),
      h('input', { name: 'nit', type: 'text', autocomplete: 'off' }),
      h('label', {}, 'Teléfono'),
      h('input', { name: 'phone', type: 'tel', inputmode: 'numeric', autocomplete: 'tel', required: true }),
      h('label', {}, 'Sucursal (escuela)'),
      h('select', { name: 'branch_id', required: true },
        ...branches.map((b) => h('option', { value: b.id }, b.name))
      ),
      h('button', { class: 'btn btn-block mt-16', onclick: handle }, h('span', { html: iconSvg(Send, 16) }), 'Enviar registro'),
    ),
    h('div', { style: { marginTop: '16px', textAlign: 'center' } },
      h('a', { href: 'javascript:void(0)', style: 'display:inline-flex;align-items:center;gap:6px', onclick: (e) => { e.preventDefault(); navigate('/login'); } }, h('span', { html: iconSvg(ArrowLeft, 14) }), 'Volver a iniciar sesión'),
    ),
  );
  view.appendChild(h('div', { class: 'auth-wrap' }, card));

  async function handle() {
    const values = {
      full_name: card.querySelector('[name="full_name"]').value.trim(),
      email: card.querySelector('[name="email"]').value.trim(),
      branch_id: card.querySelector('[name="branch_id"]').value,
      document_number: card.querySelector('[name="document_number"]').value.trim(),
      phone: card.querySelector('[name="phone"]').value.trim(),
    };
    const errors = validateForm(values, registerRules);
    if (Object.keys(errors).length) { showFieldErrors(card, errors); return; }
    const payload = {
      full_name: values.full_name,
      email: values.email,
      document_number: card.querySelector('[name="document_number"]').value.trim(),
      nit: card.querySelector('[name="nit"]').value.trim() || undefined,
      phone: card.querySelector('[name="phone"]').value.trim(),
      branch_id: Number(values.branch_id),
    };
    try {
      const { RegistrationRequestAPI } = await import('../services/endpoints.js');
      await RegistrationRequestAPI.submitPublic(payload);
      toast('Registro enviado. Un administrador lo revisará.', 'success');
      navigate('/login');
    } catch (err) {
      toast(err.message, 'error');
    }
  }
};
